import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, Overline, Chapter } from "../components/Reveal";
import { DemoForm } from "../components/EnquiryForms";

const PROBLEMS = [
    "Projects spread across different files.",
    "Inventory managed through spreadsheets.",
    "Property information shared through WhatsApp.",
    "Teams working from different versions of information.",
    "Sales and operational data becoming difficult to track as portfolios grow.",
];

const CAPABILITIES = [
    "Developers",
    "Projects",
    "Registrations",
    "Phases",
    "Towers",
    "Configurations",
    "Units",
    "Inventory",
    "Payment Plans",
    "Amenities",
    "Media",
    "Documents",
];

const AUDIENCES = [
    {
        t: "Developers",
        c: "Structure projects, phases, towers, configurations, units and supporting project information within one organised platform.",
    },
    {
        t: "Brokerages & Channel Partners",
        c: "Organise multiple projects and property opportunities within a structured real estate operating environment.",
    },
    {
        t: "Real Estate Advisories",
        c: "Create a central system for organising projects, opportunities and property information across the business.",
    },
];

const ScreenshotFrame = ({ number, title, desc, src }) => (
    <Reveal>
        <div className="border border-ink/15 bg-white" data-testid={`screenshot-frame-${number}`}>
            <div className="flex items-center gap-1.5 border-b border-ink/10 px-4 py-3">
                <span className="h-2 w-2 rounded-full bg-ink/15" />
                <span className="h-2 w-2 rounded-full bg-ink/15" />
                <span className="h-2 w-2 rounded-full bg-ink/15" />
                <span className="ml-3 text-[10px] font-medium uppercase tracking-[0.25em] text-ink/45">{title}</span>
            </div>
            {src ? (
                <div className="aspect-[16/10] overflow-hidden bg-secondary">
                    <img src={src} alt={title} className="h-full w-full object-cover" loading="lazy" decoding="async" />
                </div>
            ) : (
                <div className="flex aspect-[16/10] items-center justify-center bg-[#F3F1EC] p-8">
                    <div className="flex h-full w-full flex-col items-center justify-center gap-3 border border-dashed border-ink/20 text-center">
                        <span className="font-serif text-lg font-light italic text-ink/45">Platform Screenshot</span>
                        <span className="text-[10px] font-medium uppercase tracking-[0.25em] text-ink/40">
                            To be added
                        </span>
                    </div>
                </div>
            )}
        </div>
        <div className="mt-5 flex items-baseline gap-4">
            <span className="font-serif text-sm italic text-gold">{number}</span>
            <div>
                <h3 className="font-serif text-xl font-medium tracking-tight text-ink">{title}</h3>
                <p className="mt-1 text-[15px] font-light leading-relaxed text-ink/70">{desc}</p>
            </div>
        </div>
    </Reveal>
);

const Technology = () => {
    useEffect(() => {
        document.title = "LUXATI Technology | Real Estate Platform & White Label Solutions";
        const meta = document.querySelector('meta[name="description"]');
        const prev = meta?.getAttribute("content");
        meta?.setAttribute(
            "content",
            "Explore LUXATI Technology — a structured real estate platform for developers, brokerages and advisory businesses, available for white-label deployment."
        );
        return () => {
            document.title = "LUXATI — Real Estate Investments & Partnerships";
            if (meta && prev) meta.setAttribute("content", prev);
        };
    }, []);

    return (
        <main data-testid="technology-page">
            {/* Hero */}
            <section className="bg-charcoal pb-20 pt-40 text-ivory md:pb-28 md:pt-52">
                <div className="mx-auto max-w-[1400px] px-6 md:px-12">
                    <Reveal>
                        <Overline>LUXATI Technology</Overline>
                        <h1 className="mt-6 max-w-3xl font-serif text-5xl font-light leading-[1.02] tracking-tight sm:text-6xl">
                            Real Estate.<br />
                            <span className="italic text-gold">Organised Better.</span>
                        </h1>
                        <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-ivory/70 md:text-lg">
                            A connected real estate platform built to organise projects, inventory, sales
                            operations and teams — bringing the information that matters into one structured
                            system.
                        </p>
                        <div className="mt-12 flex flex-wrap items-center gap-4">
                            <a
                                href="#platform"
                                data-testid="tech-explore-cta"
                                className="group inline-flex items-center gap-3 bg-ivory px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ink transition-colors duration-500 hover:bg-gold"
                            >
                                Explore the Platform
                                <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
                            </a>
                            <a
                                href="#demo"
                                data-testid="tech-talk-cta"
                                className="group inline-flex items-center gap-3 border border-ivory/40 px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:border-gold hover:text-gold"
                            >
                                Talk to LUXATI
                                <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* 01 — The Problem */}
            <section className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-36">
                <Chapter number="01" label="The Problem" />
                <div className="mt-12 grid gap-12 md:grid-cols-12">
                    <Reveal className="md:col-span-6">
                        <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-ink sm:text-5xl">
                            One Platform.<br />
                            <span className="italic text-gold">One Source of Truth.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15} className="md:col-span-5 md:col-start-8">
                        <ul className="space-y-4" data-testid="problem-list">
                            {PROBLEMS.map((p) => (
                                <li key={p} className="border-b border-ink/10 pb-4 text-[15px] font-light leading-relaxed text-ink/70">
                                    {p}
                                </li>
                            ))}
                        </ul>
                        <p className="mt-10 font-serif text-3xl font-light italic tracking-tight text-ink md:text-4xl">
                            Bring it <span className="text-gold">together.</span>
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* 02 — Platform Overview */}
            <section id="platform" className="border-t border-ink/10 bg-white/50">
                <div className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-36">
                    <Chapter number="02" label="The Platform" />
                    <div className="mt-12 grid gap-12 md:grid-cols-12">
                        <Reveal className="md:col-span-5">
                            <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-ink sm:text-5xl">
                                Built Around <span className="italic text-gold">Real Estate.</span>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.15} className="md:col-span-6 md:col-start-7">
                            <p className="text-base font-light leading-relaxed text-ink/70 md:text-lg">
                                LUXATI Platform provides structured infrastructure for organising real
                                estate projects and operations — with dedicated areas for every layer of a
                                real estate business.
                            </p>
                        </Reveal>
                    </div>
                    <div className="mt-16 grid gap-px bg-ink/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="capability-list">
                        {CAPABILITIES.map((c, i) => (
                            <Reveal key={c} delay={(i % 4) * 0.08} className="bg-ivory p-7">
                                <p className="font-serif text-xs italic text-gold">{String(i + 1).padStart(2, "0")}</p>
                                <p className="mt-3 font-serif text-xl font-medium tracking-tight text-ink">{c}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* 03 — Product Visuals */}
            <section className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-36">
                <Chapter number="03" label="Inside The Platform" />
                <Reveal className="mt-12 max-w-2xl">
                    <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-ink sm:text-5xl">
                        Structure You Can <span className="italic text-gold">See.</span>
                    </h2>
                </Reveal>
                <div className="mt-16 grid gap-14 md:grid-cols-2 md:gap-x-10" data-testid="product-visuals">
                    <ScreenshotFrame
                        number="01"
                        title="Project Structure"
                        src="/platform/01-project-structure.jpeg"
                        desc="Developer → Project → Phase → Tower → Configuration → Units."
                    />
                    <ScreenshotFrame
                        number="02"
                        title="Inventory"
                        src="/platform/02-inventory.jpeg"
                        desc="Structured property inventory and unit information."
                    />
                    <ScreenshotFrame
                        number="03"
                        title="Project Information"
                        src="/platform/03-project-information.jpeg"
                        desc="Registrations, payment plans, amenities, media and documents."
                    />
                    <ScreenshotFrame
                        number="04"
                        title="Operations"
                        src="/platform/04-operations.jpeg"
                        desc="A central environment for teams to work with organised real estate information."
                    />
                </div>
            </section>

            {/* 04 — Who It Is For */}
            <section className="border-t border-ink/10 bg-white/50">
                <div className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-36">
                    <Chapter number="04" label="Who It Is For" />
                    <Reveal className="mt-12">
                        <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-ink sm:text-5xl">
                            Built For <span className="italic text-gold">Real Estate.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-16 grid gap-px bg-ink/10 md:grid-cols-3">
                        {AUDIENCES.map((a, i) => (
                            <Reveal key={a.t} delay={i * 0.12} className="group bg-ivory p-10 transition-colors duration-700 hover:bg-white md:p-12">
                                <p className="font-serif text-sm italic text-ink/40">{String(i + 1).padStart(2, "0")}</p>
                                <h3 className="mt-3 font-serif text-2xl font-medium tracking-tight text-ink">{a.t}</h3>
                                <span className="mt-5 block h-px w-8 bg-gold transition-all duration-700 group-hover:w-16" />
                                <p className="mt-5 text-[15px] font-light leading-relaxed text-ink/70">{a.c}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* 05 — White Label */}
            <section className="grain-overlay bg-charcoal text-ivory">
                <div className="mx-auto grid max-w-[1400px] gap-16 px-6 py-24 md:grid-cols-12 md:px-12 md:py-40">
                    <div className="md:col-span-6">
                        <Chapter number="05" label="White Label" dark />
                        <Reveal className="mt-10">
                            <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                                Your Brand.<br />
                                <span className="italic text-gold">Your Platform.</span>
                            </h2>
                            <p className="mt-8 max-w-md text-base font-light leading-relaxed text-ivory/70">
                                LUXATI Platform can be deployed as a white-label real estate platform for
                                developers, brokerages and advisory businesses seeking their own structured
                                technology environment.
                            </p>
                            <p className="mt-5 max-w-md text-sm font-light leading-relaxed text-ivory/50">
                                White-label deployments can be configured around your organisation's identity
                                and requirements.
                            </p>
                            <p className="mt-10 text-[11px] font-medium uppercase tracking-[0.3em] text-ivory/50">
                                White-Label Plans — Contact Us
                            </p>
                            <a
                                href="#demo"
                                data-testid="whitelabel-cta"
                                className="group mt-6 inline-flex items-center gap-3 bg-ivory px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ink transition-colors duration-500 hover:bg-gold"
                            >
                                Discuss White Label
                                <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
                            </a>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15} className="md:col-span-6">
                        <div className="border border-ivory/15 bg-ivory/[0.03]" data-testid="whitelabel-visual">
                            <div className="flex items-center gap-1.5 border-b border-ivory/10 px-4 py-3">
                                <span className="h-2 w-2 rounded-full bg-ivory/20" />
                                <span className="h-2 w-2 rounded-full bg-ivory/20" />
                                <span className="h-2 w-2 rounded-full bg-ivory/20" />
                                <span className="ml-3 text-[10px] font-light tracking-[0.2em] text-ivory/40">
                                    yourcompany.com
                                </span>
                            </div>
                            <div className="flex aspect-[16/10] flex-col items-center justify-center gap-5 p-10 text-center">
                                <p className="font-serif text-3xl font-light tracking-[0.28em] text-ivory md:text-4xl">
                                    YOUR COMPANY
                                </p>
                                <span className="h-px w-12 bg-gold/60" />
                                <p className="text-[11px] font-light uppercase tracking-[0.3em] text-ivory/50">
                                    Your Logo &nbsp;&bull;&nbsp; Your Branding &nbsp;&bull;&nbsp; Your Platform
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* 06 — Why LUXATI Technology */}
            <section className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-36">
                <Chapter number="06" label="Why LUXATI Technology" />
                <div className="mt-12 grid gap-12 md:grid-cols-12">
                    <Reveal className="md:col-span-6">
                        <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-ink sm:text-5xl">
                            Built From Real Estate.<br />
                            <span className="italic text-gold">Not Around It.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15} className="md:col-span-5 md:col-start-8">
                        <p className="text-base font-light leading-relaxed text-ink/70 md:text-lg">
                            LUXATI Technology is being developed alongside real-world real estate operations —
                            with the goal of creating clearer structure around projects, property information
                            and the way teams work.
                        </p>
                        <p className="mt-6 text-base font-light leading-relaxed text-ink/70">
                            The same structure that organises LUXATI's advisory work is available to
                            developers, brokerages and advisories — as a platform of their own.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* 07 — Demo / Contact */}
            <section id="demo" className="border-t border-ink/10 bg-white/50">
                <div className="mx-auto grid max-w-[1400px] gap-16 px-6 py-24 md:grid-cols-12 md:px-12 md:py-36">
                    <div className="md:col-span-5">
                        <Chapter number="07" label="See It In Action" />
                        <Reveal className="mt-10">
                            <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-ink sm:text-5xl">
                                See LUXATI Platform <span className="italic text-gold">In Action.</span>
                            </h2>
                            <p className="mt-8 max-w-md text-base font-light leading-relaxed text-ink/70">
                                Tell us about your organisation and we'll show you how LUXATI Platform could
                                fit your real estate operations.
                            </p>
                            <div className="mt-10 flex flex-wrap items-center gap-4">
                                <a
                                    href="#demo-form"
                                    data-testid="request-demo-cta"
                                    className="inline-flex items-center gap-3 bg-ink px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:bg-gold hover:text-ink"
                                >
                                    Request a Demo
                                </a>
                                <Link
                                    to="/contact"
                                    data-testid="contact-luxati-cta"
                                    className="inline-flex items-center gap-3 border border-ink/25 px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ink transition-colors duration-500 hover:border-gold hover:text-[#8a6a1c]"
                                >
                                    Contact LUXATI
                                </Link>
                            </div>
                            <div className="mt-12 space-y-4 border-t border-ink/10 pt-8">
                                <p className="text-[11px] font-medium uppercase tracking-[0.25em] text-ink/50">
                                    White-Label &amp; Platform Enquiries
                                </p>
                                <a
                                    href="mailto:info@luxati.com"
                                    data-testid="platform-email"
                                    className="link-underline inline-block font-serif text-2xl font-light tracking-tight text-ink"
                                >
                                    info@luxati.com
                                </a>
                                <p className="pt-4 text-[11px] font-medium uppercase tracking-[0.25em] text-ink/50">
                                    Plans &amp; Pricing
                                </p>
                                <p className="text-sm font-light text-ink/70">Contact LUXATI</p>
                            </div>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15} className="md:col-span-6 md:col-start-7" id="demo-form">
                        <DemoForm testid="demo-form" />
                    </Reveal>
                </div>
            </section>
        </main>
    );
};

export default Technology;
